import { useState, useEffect } from 'react';
import LandingPage from '../components/LandingPage';
import { detectLocale } from '../locales/config';

const FRENCH_LOCALES = ['fr-fr', 'fr-be', 'fr-lu'];

const faqItems = [
  {
    q: "Comment fonctionne le calculateur de dettes ?",
    a: "Saisissez chaque dette avec son solde, son taux d'intérêt annuel et le remboursement minimum mensuel. Le calculateur applique la méthode boule de neige ou avalanche pour vous indiquer votre date exacte de libération des dettes, le total des intérêts que vous paierez et combien vous économisez en payant un peu plus chaque mois.",
  },
  {
    q: "Quelle méthode est la meilleure : boule de neige ou avalanche ?",
    a: "Cela dépend de votre profil. La méthode avalanche (rembourser en priorité la dette au taux le plus élevé) économise davantage sur les intérêts. La méthode boule de neige (rembourser en priorité la plus petite dette) apporte des victoires rapides qui maintiennent la motivation. Les deux sont bien plus efficaces que de ne payer que le minimum.",
  },
  {
    q: "Combien de temps faudra-t-il pour rembourser mes dettes ?",
    a: "Cela dépend de votre solde, de votre taux d'intérêt et de ce que vous payez chaque mois. En ne payant que le minimum sur une carte de crédit, vous pouvez mettre plus de 10 ans à vous en libérer. En ajoutant un montant supplémentaire chaque mois, vous pouvez y parvenir en 2 à 3 ans et économiser des milliers d'euros en intérêts. Utilisez notre simulateur de remboursement de dette pour voir votre plan personnalisé.",
  },
  {
    q: "Ce calculateur est-il gratuit ?",
    a: "Le calculateur de dettes est entièrement gratuit et ne nécessite aucune inscription. Vous pouvez élaborer votre plan de remboursement de dettes en quelques secondes. Si vous souhaitez sauvegarder et télécharger votre plan personnalisé en PDF, une option de paiement unique sans abonnement est disponible.",
  },
  {
    q: "Fonctionne-t-il pour les cartes de crédit et les prêts ?",
    a: "Oui. Ce calculateur de remboursement de crédit fonctionne pour les cartes de crédit, les prêts personnels, les crédits auto, les prêts étudiants et tout type de dette avec intérêts. Vous pouvez saisir plusieurs dettes à la fois pour calculer le temps total nécessaire pour vous libérer des dettes.",
  },
];

const COLORS = { text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0" };

const sections = [
  {
    heading: 'Comment utiliser ce simulateur de remboursement de dette',
    body: "Saisissez chaque dette : solde actuel, taux d'intérêt annuel et remboursement minimum mensuel. Ajoutez tout montant supplémentaire que vous pouvez consacrer au remboursement. Le calculateur de dettes vous indique votre date de libération, le total des intérêts et comment chaque stratégie — boule de neige ou avalanche — accélère votre plan de désendettement.",
  },
  {
    heading: 'Méthode boule de neige vs avalanche : laquelle choisir ?',
    body: (
      <>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 12 }}>
          La <strong style={{ color: COLORS.text }}>méthode boule de neige des dettes</strong> consiste à
          rembourser en priorité la plus petite dette et à utiliser ce paiement libéré pour attaquer la suivante.
          Elle est idéale si vous avez besoin de motivation et de victoires rapides dans votre
          plan pour rembourser ses dettes rapidement.
        </p>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 0 }}>
          La <strong style={{ color: COLORS.text }}>méthode avalanche</strong> s'attaque en priorité à la dette
          au taux d'intérêt le plus élevé. Elle est mathématiquement plus efficace et réduit davantage
          le coût total. Utilisez notre simulateur de remboursement de dette pour calculer
          combien vous économisez avec chaque méthode et comment rembourser ses dettes rapidement.
        </p>
      </>
    ),
  },
  {
    heading: "Pourquoi les paiements minimums vous coûtent des années de liberté financière",
    body: "Les intérêts d'une carte de crédit s'accumulent chaque mois sur le solde restant dû. En ne payant que le minimum, une dette moyenne peut prendre plus de 8 à 10 ans à rembourser. Ajouter même 10 à 15 % de plus au paiement minimum peut réduire ce délai de moitié et vous faire économiser des milliers d'euros. Utilisez le calculateur de remboursement de crédit pour voir l'impact exact de chaque euro supplémentaire que vous y consacrez.",
  },
];

export default function CalculateurDeDettes() {
  const [locale, setLocale] = useState('fr-fr');

  useEffect(() => {
    detectLocale().then(detected => {
      setLocale(FRENCH_LOCALES.includes(detected) ? detected : 'fr-fr');
    });
  }, []);

  return (
    <LandingPage
      locale={locale}
      pageUrl="https://clearmydebt.app/calculateur-de-dettes/"
      helmet={{
        lang: 'fr',
        title: 'Calculateur de Dettes Gratuit — Comment Rembourser ses Dettes | ClearMyDebt',
        description: 'Calculez quand vous serez libéré de vos dettes. Méthode boule de neige et avalanche. Sans inscription. Résultats instantanés.',
        ogTitle: 'Calculateur de Dettes Gratuit — Comment Rembourser ses Dettes | ClearMyDebt',
        ogDescription: 'Calculez quand vous serez libéré de vos dettes. Méthode boule de neige et avalanche. Sans inscription. Résultats instantanés.',
      }}
      hreflangList={[
        { hreflang: 'fr-fr', href: 'https://clearmydebt.app/calculateur-de-dettes/' },
        { hreflang: 'fr-be', href: 'https://clearmydebt.app/calculateur-de-dettes/' },
        { hreflang: 'x-default', href: 'https://clearmydebt.app/calculateur-de-dettes/' },
      ]}
      h1="Calculateur de Dettes Gratuit"
      intro="Saisissez vos dettes et découvrez exactement quand vous serez libéré de vos dettes — et combien vous économiserez avec le bon plan de remboursement."
      sections={sections}
      faqItems={faqItems}
      faqHeading="Questions Fréquentes"
      moreHeading="Plus de calculateurs"
      moreLinks={[
        { href: '/fr-fr/credit-card-payoff-calculator/', label: 'Calculateur carte de crédit' },
        { href: '/fr-fr/debt-snowball-calculator/', label: 'Méthode boule de neige' },
        { href: '/fr-fr/debt-avalanche-calculator/', label: 'Méthode avalanche' },
        { href: '/fr-fr/debt-repayment-calculator/', label: 'Calculateur de remboursement' },
      ]}
    />
  );
}
